import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface ComboBoxOption {
  id: string;
  label: string;
  email?: string;
}

export interface ComboBoxProps {
  options: ComboBoxOption[];
  value: string;
  onChange: (value: string, type: 'USER' | 'TEXT') => void;
  placeholder?: string;
  allowFreeText?: boolean;
  label?: string;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
}

const ComboBox = forwardRef<HTMLInputElement, ComboBoxProps>(({
  options,
  value,
  onChange,
  placeholder = 'Select or enter...',
  allowFreeText = true,
  label,
  error,
  helperText,
  disabled = false,
  fullWidth = false,
  className,
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedType, setSelectedType] = useState<'USER' | 'TEXT'>('USER');
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Initialize input value based on current value
  useEffect(() => {
    const selectedOption = options.find(opt => opt.id === value);
    if (selectedOption) {
      setInputValue(selectedOption.label);
      setSelectedType('USER');
    } else if (value) {
      setInputValue(value);
      setSelectedType('TEXT');
    } else {
      setInputValue('');
    }
  }, [value, options]);

  // Filter options based on input
  useEffect(() => {
    if (!inputValue) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option =>
        option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
        (option.email && option.email.toLowerCase().includes(inputValue.toLowerCase()))
      );
      setFilteredOptions(filtered);
    }
  }, [inputValue, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);

    // If input matches an option exactly, select it as USER type
    const exactMatch = options.find(opt => 
      opt.label.toLowerCase() === newValue.toLowerCase()
    );
    
    if (exactMatch) {
      setSelectedType('USER');
      onChange(exactMatch.id, 'USER');
    } else if (allowFreeText) {
      setSelectedType('TEXT');
      onChange(newValue, 'TEXT');
    }
  };

  const handleOptionSelect = (option: ComboBoxOption) => {
    setInputValue(option.label);

    // Check if this is a manual entry (previously used)
    const isManualEntry = option.email === '(Previously used)';
    const type = isManualEntry ? 'TEXT' : 'USER';
    const value = isManualEntry ? option.label : option.id;

    setSelectedType(type);
    onChange(value, type);
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'ArrowDown' && !isOpen) {
      setIsOpen(true);
    }
  };

  const displayValue = () => {
    const selectedOption = options.find(opt => opt.id === value);
    if (selectedOption) {
      return selectedOption.label;
    }
    return value || '';
  };

  const comboId = `combo-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={cn('space-y-1', fullWidth && 'w-full', className)}>
      {label && (
        <label 
          htmlFor={comboId}
          className="block text-sm font-medium text-[var(--text)]"
        >
          {label}
        </label>
      )}
      
      <div className="relative">
        <input
          ref={ref || inputRef}
          id={comboId}
          type="text"
          value={isOpen ? inputValue : displayValue()}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'block w-full rounded-md border bg-[var(--surface)] px-3 py-2 pr-10 text-sm text-[var(--text)] placeholder-[var(--muted)] transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] focus:ring-offset-2 focus:ring-offset-[var(--bg)]',
            error
              ? 'border-status-error focus:ring-status-error'
              : 'border-[var(--border)] hover:border-[var(--accent)]/60',
            disabled && 'bg-[var(--surface-muted)] cursor-not-allowed opacity-50'
          )}
        />
        
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-[var(--muted)] hover:text-[var(--text)] disabled:cursor-not-allowed"
        >
          <ChevronDown 
            className={cn(
              'h-4 w-4 transition-transform',
              isOpen && 'rotate-180'
            )} 
          />
        </button>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-[var(--surface)] border border-[var(--border)] rounded-lg shadow-lg max-h-60 overflow-auto"
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => handleOptionSelect(option)}
                className="w-full px-3 py-2 text-left hover:bg-[var(--surface-muted)] focus:bg-[var(--surface-muted)] focus:outline-none flex items-center justify-between first:rounded-t-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-[var(--text)]">{option.label}</div>
                    {option.email === '(Previously used)' && (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-accent-blue/20 text-accent-blue border border-accent-blue/30">
                        Previously used
                      </span>
                    )}
                  </div>
                  {option.email && option.email !== '(Previously used)' && (
                    <div className="text-sm text-[var(--muted)]">{option.email}</div>
                  )}
                </div>
                {value === option.id && (
                  <Check className="h-4 w-4 text-[var(--accent)]" />
                )}
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-[var(--muted)]">
              {allowFreeText ? (
                <div>
                  <div className="text-sm">No users found.</div>
                  <div className="text-xs mt-1">
                    Continue typing to enter manually.
                  </div>
                </div>
              ) : (
                'No options found'
              )}
            </div>
          )}
          
          {/* Show current text input as option if it doesn't match any user */}
          {allowFreeText && inputValue && !options.some(opt => 
            opt.label.toLowerCase() === inputValue.toLowerCase()
          ) && (
            <button
              type="button"
              onClick={() => {
                setSelectedType('TEXT');
                onChange(inputValue, 'TEXT');
                setIsOpen(false);
              }}
              className="w-full px-3 py-2 text-left hover:bg-[var(--surface-muted)] focus:bg-[var(--surface-muted)] focus:outline-none border-t border-[var(--border)] last:rounded-b-lg"
            >
              <div className="flex items-center">
                <div className="flex-1">
                  <div className="font-medium text-[var(--text)]">"{inputValue}"</div>
                  <div className="text-xs text-[var(--muted)]">Enter as manual text</div>
                </div>
                {selectedType === 'TEXT' && value === inputValue && (
                  <Check className="h-4 w-4 text-accent-blue" />
                )}
              </div>
            </button>
          )}
        </div>
      )}

      {/* Type indicator */}
      {value && (
        <div className="text-xs text-[var(--muted)]">
          {selectedType === 'USER' ? (
            <span className="text-emerald-500">✓ Selected user</span>
          ) : (
            <span className="text-[var(--accent)]">✓ Manual entry</span>
          )}
        </div>
      )}

      {/* Error message */}
      {error && (
        <p className="text-xs text-status-error font-medium">{error}</p>
      )}
      
      {/* Helper text */}
      {helperText && !error && (
        <p className="text-sm text-[var(--muted)]">{helperText}</p>
      )}
    </div>
  );
});

ComboBox.displayName = 'ComboBox';

export default ComboBox;
